/**
 * Generates a deterministic HSL color from a number.
 * Same input => same color as long as the seed is the same and not falsy
 */
function numberToHsl(
  seed: number,
  options?: {
    saturation?: number; // 0–100
    lightness?: number; // 0–100
  }
): string {
  const saturation = options?.saturation ?? 65;
  const lightness = options?.lightness ?? 55;

  // Simple hash to spread values
  const hash = Math.abs(Math.sin(seed || Math.random()) * 10000);

  // Hue: 0–359
  const hue = Math.floor(hash % 360);

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/**
 * Adds absolutely spaced elements representing each page with different background colors
 * Can be used to visualize the pages and spacing between them.
 * Requires debug to be true and manually commented out
 */
const addPageSpacers = (pageHeight: number, contentDocument: Document) => {
  const bodyHeight = contentDocument.body.getBoundingClientRect().height;

  const numberOfPages = Math.ceil(bodyHeight / pageHeight);

  // Adds absolutely spaced elements representing each page with different background colors
  // Can be used to visualize the pages and spacing between them
  for (let i = 0; i < numberOfPages; i++) {
    const spacingElement = document.createElement('div');
    spacingElement.style.position = 'absolute';
    spacingElement.style.top = `${i * pageHeight}px`;
    spacingElement.style.left = '0';
    spacingElement.style.width = '100%';
    spacingElement.style.height = `${pageHeight}px`;
    spacingElement.style.backgroundColor = numberToHsl(i);
    spacingElement.style.zIndex = '-1';
    // Add a data attribute to the spacing element to indicate the page number
    spacingElement.setAttribute('data-printty-page-number', i.toString());
    // Add a data attribute to the spacing element to indicate the page height
    spacingElement.setAttribute('data-printty-page-height', pageHeight.toString());

    // Needs to be uncommented to see the page spacers
    contentDocument.body.appendChild(spacingElement);
  }
};

/**
 * Creates dynamic spacing between elements to prevent fragmentation
 * @param elements - The elements to create spacing between
 * @param pageHeight - The height of each page
 * @param gap - The gap between each element
 * @param debug - Whether to add page spacers for debugging
 * @param contentDocument - The document to create the spacing in
 */
const createDynamicSpacing = async (
  elements: NodeListOf<Element | HTMLElement>,
  pageHeight: number,
  gap: number,
  debug: boolean,
  contentDocument: Document
) => {
  await Array.from(elements).reduce(async (promise, element, index) => {
    await promise;

    // Insert gap between all elements except the first one
    if (gap && index !== 0) {
      const spacingElement = document.createElement('div');
      spacingElement.style.height = `${gap}px`;
      element.parentNode?.insertBefore(spacingElement, element);
    }

    const elementRect = element.getBoundingClientRect();
    const spaceFromTop = elementRect.top;
    const elementHeight = elementRect.height;

    const currentPage = Math.floor(spaceFromTop / pageHeight);
    const spaceLeftOnPage = (currentPage + 1) * pageHeight - spaceFromTop;

    if (debug) {
      element.setAttribute('data-printty-space-from-top', spaceFromTop.toString());
      if ('style' in element) {
        element.style.border = `1px solid ${numberToHsl(index)}`;
      }
    }

    // If element doesn't fit on current page, move it to next page
    if (elementHeight > spaceLeftOnPage) {
      const spacingElement = document.createElement('div');
      const extraSpaceNeeded = pageHeight - (spaceFromTop % pageHeight);
      spacingElement.style.height = `${extraSpaceNeeded}px`;
      if (debug) {
        spacingElement.style.backgroundColor = numberToHsl(index);
        spacingElement.setAttribute('data-printty-page-number', currentPage.toString());
        spacingElement.setAttribute('data-printty-space-from-top', spaceFromTop.toString());
        spacingElement.setAttribute('data-printty-space-left-on-page', spaceLeftOnPage.toString());
      }
      element.parentNode?.insertBefore(spacingElement, element);
    }
    return Promise.resolve();
  }, Promise.resolve());

  if (debug) {
    addPageSpacers(pageHeight, contentDocument);
  }
  return true;
};

export { createDynamicSpacing };
